import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  fetchReportEmailPreference,
  saveReportEmailPreference,
} from '../services/notifications.api';

/**
 * The reader's notification feed.
 *
 * Polled rather than pushed: a new edition appears a few times a quarter, so a
 * minute of latency costs nothing and a socket would be machinery for nothing.
 * The query is keyed on the language because the server returns the copy in the
 * language asked for, so switching FR/EN refetches rather than showing stale
 * English text.
 */
export function useNotifications() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  return useQuery({
    queryKey: ['notifications', lang],
    queryFn: () => fetchNotifications(lang),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/** Whether this reader asked to be emailed when a new edition is published. */
export function useReportEmailPreference() {
  return useQuery({
    queryKey: ['report-email-preference'],
    queryFn: fetchReportEmailPreference,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveReportEmailPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (wants: boolean) => saveReportEmailPreference(wants),
    onSuccess: (value) => qc.setQueryData(['report-email-preference'], value),
  });
}
